import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { Channel } from '../types/channel';
import { toast } from '../lib/toast';

interface Subscription {
  id: string;
  channelId: string;
  subscriberEmail: string;
  createdAt: string;
}

interface UseSubscriptionsResult {
  subscriptions: Subscription[];
  loading: boolean;
  refresh: () => void;
}

interface UseSubscribeResult {
  subscribed: boolean;
  loading: boolean;
  /** Latest count from the server after a subscribe/unsubscribe; null until then. */
  subscriberCount: number | null;
  toggle: () => Promise<void>;
}

export const useSubscriptions = (): UseSubscriptionsResult => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const result = await apiClient.listMySubscriptions();
    if (result.data) setSubscriptions(result.data as Subscription[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { subscriptions, loading, refresh: fetch };
};

export const useSubscribe = (channel: Channel | null, myEmail?: string | null): UseSubscribeResult => {
  const { t } = useTranslation();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState<number | null>(null);
  // Start as true so the button stays disabled until the initial check completes
  const [loading, setLoading] = useState(true);
  // The channel the latest request belongs to — responses for a previous channel are ignored.
  const currentChannelRef = useRef<string | null>(null);

  const isOwn = !!channel && !!myEmail && channel.ownerEmail?.toLowerCase() === myEmail.toLowerCase();

  // Re-check only when the channel itself changes, not when its object is re-created.
  const channelId = channel?.id ?? null;
  const channelSlug = channel?.slug ?? null;
  useEffect(() => {
    currentChannelRef.current = channelId;
    setSubscriberCount(null);
    if (!channelId || !channelSlug || !myEmail || isOwn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient.checkSubscription(channelId, channelSlug).then((result) => {
      if (currentChannelRef.current !== channelId) return;
      setSubscribed(!!result.data?.subscribed);
    }).finally(() => {
      if (currentChannelRef.current === channelId) setLoading(false);
    });
  }, [channelId, channelSlug, myEmail, isOwn]);

  const toggle = useCallback(async () => {
    if (!channel || !myEmail || loading) return;
    const channelId = channel.id;
    setLoading(true);
    try {
      const result = subscribed
        ? await apiClient.unsubscribeChannel(channelId, channel.slug)
        : await apiClient.subscribeChannel(channelId, channel.slug);
      if (result.error) throw result.error;
      if (currentChannelRef.current !== channelId) return;
      const nowSubscribed = result.data?.subscribed ?? !subscribed;
      setSubscribed(nowSubscribed);
      if (typeof result.data?.subscriberCount === 'number') setSubscriberCount(result.data.subscriberCount);
      toast.success(nowSubscribed
        ? t('channel.subscribed_toast', { name: channel.displayName, defaultValue: 'Subscribed to {{name}}' })
        : t('channel.unsubscribed_toast', { name: channel.displayName, defaultValue: 'Unsubscribed from {{name}}' }));
    } catch (err) {
      console.warn('[useSubscribe] toggle failed:', err);
      toast.error(t('channel.subscribe_error', 'Could not update your subscription. Please try again.'));
    } finally {
      if (currentChannelRef.current === channelId) setLoading(false);
    }
  }, [channel, myEmail, subscribed, loading, t]);

  return { subscribed, loading, subscriberCount, toggle };
};
